export const PRODUCT_UPDATE_CATEGORIES = ['feature', 'improvement', 'fix', 'announcement'] as const;

export type ProductUpdateCategory = (typeof PRODUCT_UPDATE_CATEGORIES)[number];

export interface ProductUpdate {
  id: string;
  publishedAt: string;
  category: ProductUpdateCategory;
  title: string;
  summary: string;
  details: readonly string[];
  tags: readonly string[];
  href?: string;
  ctaLabel?: string;
}

export interface ProductUpdateListInput {
  since?: string;
  categories?: readonly ProductUpdateCategory[];
  query?: string;
  limit?: number;
}

export const PRODUCT_UPDATE_CATEGORY_LABELS: Record<ProductUpdateCategory, string> = {
  feature: 'New feature',
  improvement: 'Improvement',
  fix: 'Fix',
  announcement: 'Announcement',
};

/**
 * Customer-facing product update source of truth.
 *
 * Keep entries factual, concise, and newest-first. This catalog powers both the
 * in-product drawer and the hosted Attune MCP tool.
 */
export const PRODUCT_UPDATES: readonly ProductUpdate[] = [
  {
    id: '2026-07-24-product-updates',
    publishedAt: '2026-07-24T19:00:00.000Z',
    category: 'feature',
    title: 'Product updates, in one place',
    summary:
      'A new What’s new panel keeps launches, improvements, announcements, and fixes close at hand.',
    details: [
      'Open the bell in the global Attune header to read the latest updates.',
      'A per-user unread indicator makes newly published notes easy to spot.',
      'Attune AI reads this same feed through MCP, so you can ask “What’s new in Attune?”',
    ],
    tags: ['whats new', 'release notes', 'notifications', 'mcp', 'attune ai'],
  },
  {
    id: '2026-07-24-growth-program-approvals',
    publishedAt: '2026-07-24T17:00:00.000Z',
    category: 'fix',
    title: 'More reliable Growth Program approvals',
    summary:
      'Reviewed Growth Program proposals now stay correctly bound through approval, avoiding false change warnings.',
    details: [
      'Approval checks now compare the normalized proposal that was actually reviewed.',
      'Equivalent prepared proposal bindings no longer create approval hash drift.',
    ],
    tags: ['growth program', 'approval', 'proposal', 'bug fix'],
    href: '/app/workspace?mode=growth-program',
    ctaLabel: 'Open Growth Program',
  },
  {
    id: '2026-07-22-youtube-citation-links',
    publishedAt: '2026-07-22T18:00:00.000Z',
    category: 'fix',
    title: 'YouTube citations keep the right video identity',
    summary:
      'YouTube citation links now preserve each video’s canonical identity across collection and reporting.',
    details: [
      'Short, watch, embed, and mobile YouTube URLs resolve consistently.',
      'Citation reports and research sources now point back to the intended video.',
    ],
    tags: ['youtube', 'citations', 'links', 'reporting', 'bug fix'],
    href: '/app/citations',
    ctaLabel: 'View citations',
  },
  {
    id: '2026-07-19-research-steering-feedback',
    publishedAt: '2026-07-19T20:00:00.000Z',
    category: 'improvement',
    title: 'Accepted research steers appear immediately',
    summary:
      'When you guide an active research run, Attune now shows the accepted instruction without waiting for the next model update.',
    details: [
      'Accepted steering is reflected immediately in the conversation.',
      'The active run continues with the instruction attached to its current turn.',
    ],
    tags: ['research', 'steering', 'ai', 'chat', 'feedback'],
    href: '/app/workspace?new_chat=true',
    ctaLabel: 'Start a task',
  },
  {
    id: '2026-07-19-workspace-home-performance',
    publishedAt: '2026-07-19T17:00:00.000Z',
    category: 'improvement',
    title: 'A faster, steadier workspace Home',
    summary:
      'Workspace Home now loads its growth context more efficiently and holds its layout steady while data resolves.',
    details: [
      'Growth Inbox Home reads use a tighter query path.',
      'Loading states no longer shift the main workspace layout.',
    ],
    tags: ['workspace home', 'performance', 'loading', 'growth inbox'],
    href: '/app/workspace',
    ctaLabel: 'Open Home',
  },
  {
    id: '2026-07-18-growth-programs',
    publishedAt: '2026-07-18T18:00:00.000Z',
    category: 'feature',
    title: 'Growth Programs connect strategy to governed work',
    summary:
      'Attune can now turn approved growth strategy into measurable programs, coordinated work, and explicit decision points.',
    details: [
      'Build a North Star-aligned Growth Program from connected evidence.',
      'Review proposals before activation and preserve the approved scope.',
      'Track interventions, work lineage, metrics, and promotion decisions in one cockpit.',
    ],
    tags: ['growth program', 'ai cmo', 'strategy', 'governance', 'metrics'],
    href: '/app/workspace?mode=growth-program',
    ctaLabel: 'Explore Growth Program',
  },
] as const;

export class ProductUpdateInputError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ProductUpdateInputError';
  }
}

function normalizeCategories(value: unknown): ProductUpdateCategory[] {
  if (value === undefined) return [];
  if (!Array.isArray(value)) {
    throw new ProductUpdateInputError('categories must be an array.');
  }

  const categories = value.map(item => String(item).trim().toLowerCase());
  const invalid = categories.filter(
    category => !PRODUCT_UPDATE_CATEGORIES.includes(category as ProductUpdateCategory)
  );
  if (invalid.length > 0) {
    throw new ProductUpdateInputError(
      `Unsupported categories: ${Array.from(new Set(invalid)).join(', ')}.`
    );
  }
  return Array.from(new Set(categories)) as ProductUpdateCategory[];
}

function normalizeLimit(value: unknown): number {
  if (value === undefined) return 10;
  if (typeof value !== 'number' || !Number.isInteger(value) || value < 1 || value > 20) {
    throw new ProductUpdateInputError('limit must be an integer between 1 and 20.');
  }
  return value;
}

function normalizeSince(value: unknown): { value?: string; timestamp?: number } {
  if (value === undefined) return {};
  if (typeof value !== 'string' || !value.trim()) {
    throw new ProductUpdateInputError('since must be a non-empty ISO-8601 date or timestamp.');
  }
  const normalized = value.trim();
  const timestamp = Date.parse(normalized);
  if (!Number.isFinite(timestamp)) {
    throw new ProductUpdateInputError('since must be a valid ISO-8601 date or timestamp.');
  }
  return { value: normalized, timestamp };
}

export function filterProductUpdates(
  source: readonly ProductUpdate[],
  input: Record<string, unknown> = {}
) {
  const categories = normalizeCategories(input.categories);
  const limit = normalizeLimit(input.limit);
  const since = normalizeSince(input.since);
  const query = typeof input.query === 'string' ? input.query.trim() : '';
  if (input.query !== undefined && typeof input.query !== 'string') {
    throw new ProductUpdateInputError('query must be a string.');
  }

  const normalizedQuery = query.toLowerCase();
  const matching = [...source]
    .sort((a, b) => Date.parse(b.publishedAt) - Date.parse(a.publishedAt))
    .filter(update => {
      if (since.timestamp !== undefined && Date.parse(update.publishedAt) < since.timestamp) {
        return false;
      }
      if (categories.length > 0 && !categories.includes(update.category)) {
        return false;
      }
      if (!normalizedQuery) return true;

      const searchable = [
        update.title,
        update.summary,
        ...update.details,
        ...update.tags,
        PRODUCT_UPDATE_CATEGORY_LABELS[update.category],
      ]
        .join(' ')
        .toLowerCase();
      return searchable.includes(normalizedQuery);
    });
  const updates = matching.slice(0, limit);

  return {
    latestPublishedAt: source.reduce<string | null>((latest, update) => {
      if (!latest || Date.parse(update.publishedAt) > Date.parse(latest)) {
        return update.publishedAt;
      }
      return latest;
    }, null),
    availableCategories: PRODUCT_UPDATE_CATEGORIES.map(category => ({
      id: category,
      label: PRODUCT_UPDATE_CATEGORY_LABELS[category],
    })),
    appliedFilters: {
      ...(since.value ? { since: since.value } : {}),
      ...(categories.length > 0 ? { categories } : {}),
      ...(query ? { query } : {}),
      limit,
    },
    count: updates.length,
    totalMatching: matching.length,
    hasMore: matching.length > updates.length,
    updates,
  };
}

export function listProductUpdates(input: Record<string, unknown> = {}) {
  return filterProductUpdates(PRODUCT_UPDATES, input);
}
